import { useState, useEffect, useMemo } from 'react';
import { parseISO } from 'date-fns';
import { formatCustomSince } from 'utilities';

const getUpdateInterval = (date: Date): number => {
  const diffInSeconds = (Date.now() - date.getTime()) / 1000;

  if (diffInSeconds < 60) return 1000; // 1 second for "just now"
  if (diffInSeconds < 3600) return 60 * 1000; // 1 minute
  if (diffInSeconds < 86400) return 60 * 60 * 1000; // 1 hour

  return -1;
};

export function useTimeAgo(isoDate: string): string {
  // 1. Parse the date only when the isoDate string changes
  const date = useMemo(() => parseISO(isoDate), [isoDate]);

  // 2. Store a "now" value in state to trigger re-renders
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // 3. Set up an interval that updates the "now" state
    const interval = getUpdateInterval(date);

    if (interval > 0) {
      const timerId = setInterval(() => setNow(new Date()), interval);
      // Cleanup function to clear the interval
      return () => clearInterval(timerId);
    }
  }, [date]); // The effect re-runs if the date prop changes

  // 4. On each render, call the pure formatting function
  return formatCustomSince(date, now);
}
